import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TalkToAyushDialog } from "@/components/TalkToAyushDialog";
import { BrandAvatar } from "@/components/BrandAvatar";

const LINKS = [
  { to: "/tools", label: "Tools" },
  { to: "/games", label: "Learn & Play" },
  { to: "/blog", label: "Journal" },
  { to: "/news", label: "News" },
  { to: "/#faq", label: "FAQ" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location]);

  const goHash = (e, to) => {
    if (to.startsWith("/#")) {
      e.preventDefault();
      const id = to.slice(2);
      if (location.pathname !== "/") { navigate("/"); setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300); }
      else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Only the home hero is dark; on it (before scrolling) use light text.
  const overHero = location.pathname === "/" && !scrolled;

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${overHero ? "bg-transparent" : "border-b border-border bg-background/80 backdrop-blur-xl"}`}
    >
      <div className="container-px mx-auto flex h-20 max-w-[1600px] items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="group flex items-center gap-3">
          <BrandAvatar className="h-10 w-10" ring={overHero ? "ring-2 ring-white/70" : "ring-2 ring-foreground/20"} />
          <span className={`block font-display text-[11px] font-extrabold uppercase leading-none tracking-[0.2em] sm:text-sm ${overHero ? "text-white" : "text-foreground"}`}>
            Real Estate<br /><span className={overHero ? "text-white/60" : "text-muted-foreground"}>with Ayush</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={(e) => goHash(e, l.to)}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}
              className={`group relative font-mono text-xs uppercase tracking-widest transition-colors ${overHero ? "text-white/70 hover:text-white" : "text-muted-foreground hover:text-foreground"}`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-foreground transition-all duration-300 group-hover:w-full" />
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a href="https://www.instagram.com/realestatewithayush/" target="_blank" rel="noreferrer"
             data-testid="nav-instagram"
             className={`hidden items-center gap-2 border px-3 py-2 font-mono text-xs uppercase tracking-widest transition-colors sm:flex ${overHero ? "border-white/30 text-white hover:border-white" : "border-border text-foreground hover:border-foreground"}`}>
            <Instagram className="h-4 w-4" /> 6k+
          </a>
          <TalkToAyushDialog source="navbar" trigger={
            <Button data-testid="nav-cta" className={`hidden rounded-none font-mono text-xs uppercase tracking-widest md:inline-flex ${overHero ? "bg-white text-black hover:bg-white/85" : "bg-foreground text-background hover:bg-foreground/85"}`}>
              Talk to Ayush
            </Button>
          } />
          <button data-testid="mobile-menu-btn" className="lg:hidden" onClick={() => setOpen(!open)}>
            {open ? <X className={`h-6 w-6 ${overHero ? "text-white" : "text-foreground"}`} /> : <Menu className={`h-6 w-6 ${overHero ? "text-white" : "text-foreground"}`} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-border bg-background lg:hidden" data-testid="mobile-menu">
            <div className="container-px flex flex-col gap-1 py-4">
              {LINKS.map((l) => (
                <Link key={l.to} to={l.to} onClick={(e) => goHash(e, l.to)}
                      className="border-b border-border py-4 font-display text-2xl font-bold uppercase text-foreground">
                  {l.label}
                </Link>
              ))}
              <a href="https://www.instagram.com/realestatewithayush/" target="_blank" rel="noreferrer"
                 className="py-4 font-mono text-sm uppercase tracking-widest text-foreground">Instagram · 6k+</a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
